import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, ShieldAlert, Clock, FileText, BookOpen, 
  User, Building, FileCheck, AlertCircle, CheckCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OWNERSHIP_TYPES = [
  { value: 'original_author', label: 'Autor Original', icon: User, description: 'Soy el autor/creador de la obra' },
  { value: 'publisher', label: 'Editorial', icon: Building, description: 'Represento a una editorial' },
  { value: 'rights_holder', label: 'Titular de Derechos', icon: FileCheck, description: 'Tengo cesión de derechos' }
];

const AuthorVerification = ({ token, onVerificationComplete }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    work_title: '',
    work_identifier: '',
    work_url: '',
    ownership_type: 'original_author',
    additional_notes: ''
  });

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/author/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      if (response.data.author_verified) {
        onVerificationComplete?.(true);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.work_title.trim()) {
      toast.error('El título de la obra es requerido');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/author/submit-verification`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Solicitud de verificación enviada');
      setShowForm(false);
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-viubux-dark border-yellow-500/30">
        <CardContent className="pt-6 text-center text-white/60">
          Cargando estado de verificación...
        </CardContent>
      </Card>
    );
  }

  // Si ya está verificado
  if (status?.author_verified) {
    return (
      <Card className="card-viubux-dark border-green-500/30" data-testid="verification-approved">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-500/20 p-3 rounded-full">
              <ShieldCheck className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h3 className="gotham-bold text-lg text-green-400">Autor Verificado</h3>
              <p className="text-white/60 text-sm">Tu cuenta ha sido verificada. Puedes continuar con tu inscripción.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Si tiene solicitud pendiente
  if (status?.status === 'pending') {
    return (
      <Card className="card-viubux-dark border-yellow-500/30" data-testid="verification-pending">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-yellow-500/20 p-3 rounded-full">
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
            <div>
              <h3 className="gotham-bold text-lg text-yellow-400">Verificación en Proceso</h3>
              <p className="text-white/60 text-sm">Tu solicitud está siendo revisada por nuestro equipo.</p>
            </div>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
            <p className="text-yellow-200 text-sm">
              📋 Te notificaremos por email cuando tu solicitud sea procesada. 
              Normalmente toma 24-48 horas.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Si fue rechazado
  if (status?.status === 'rejected') {
    return (
      <Card className="card-viubux-dark border-red-500/30" data-testid="verification-rejected">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-red-500/20 p-3 rounded-full">
              <ShieldAlert className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h3 className="gotham-bold text-lg text-red-400">Verificación No Aprobada</h3>
              <p className="text-white/60 text-sm">Tu solicitud anterior no fue aprobada.</p>
            </div>
          </div>
          {status.rejection_reason && (
            <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30 mb-4">
              <p className="text-red-200 text-sm">
                <strong>Motivo:</strong> {status.rejection_reason}
              </p>
            </div>
          )}
          <Button
            onClick={() => setShowForm(true)}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar Nueva Solicitud
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Si no ha enviado solicitud (no_request) o mostrar formulario
  if (status?.status === 'no_request' || showForm) {
    return (
      <Card className="card-viubux-dark border-purple-500/30" data-testid="verification-form">
        <CardHeader>
          <CardTitle className="gotham-bold text-xl text-white flex items-center gap-3">
            <FileText className="w-6 h-6 text-purple-400" />
            Verificación de Autor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm mb-2">
                  <strong>¿Por qué necesitamos verificarte?</strong>
                </p>
                <p className="text-white/70 text-sm">
                  Para proteger la integridad de nuestra comunidad, verificamos que cada autor tenga 
                  una obra publicada o derechos sobre contenido. Esto previene cuentas falsas y 
                  asegura la calidad del programa.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Título de la obra */}
            <div>
              <label className="block text-white/70 text-sm mb-2">
                Título de tu obra publicada *
              </label>
              <input
                type="text"
                value={formData.work_title}
                onChange={(e) => setFormData({ ...formData, work_title: e.target.value })}
                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                placeholder="Ej: El Arte de la Productividad"
                required
                data-testid="work-title-input"
              />
            </div>

            {/* Identificador */}
            <div>
              <label className="block text-white/70 text-sm mb-2">
                ISBN o Identificador (opcional)
              </label>
              <input
                type="text"
                value={formData.work_identifier}
                onChange={(e) => setFormData({ ...formData, work_identifier: e.target.value })}
                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                placeholder="Ej: 978-84-XXXXXXX-X"
              />
            </div>

            {/* URL de la obra */}
            <div>
              <label className="block text-white/70 text-sm mb-2">
                Link en app.viubux.com (opcional)
              </label>
              <input
                type="url"
                value={formData.work_url}
                onChange={(e) => setFormData({ ...formData, work_url: e.target.value })}
                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                placeholder="https://app.viubux.com/..."
              />
            </div>

            {/* Tipo de titularidad */}
            <div>
              <label className="block text-white/70 text-sm mb-2">
                Tipo de titularidad *
              </label>
              <div className="grid gap-3">
                {OWNERSHIP_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.ownership_type === type.value;
                  return (
                    <label
                      key={type.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-purple-500/20 border-purple-500'
                          : 'bg-black/20 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ownership_type"
                        value={type.value}
                        checked={isSelected}
                        onChange={(e) => setFormData({ ...formData, ownership_type: e.target.value })}
                        className="sr-only"
                      />
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-purple-400' : 'text-white/40'}`} />
                      <div>
                        <p className={`font-medium ${isSelected ? 'text-white' : 'text-white/70'}`}>
                          {type.label}
                        </p>
                        <p className="text-white/40 text-xs">{type.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Notas adicionales */}
            <div>
              <label className="block text-white/70 text-sm mb-2">
                Notas adicionales (opcional)
              </label>
              <textarea
                value={formData.additional_notes}
                onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-purple-500 min-h-[100px]"
                placeholder="Información adicional que quieras compartir..."
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !formData.work_title.trim()}
              className="w-full btn-viubux"
              data-testid="submit-verification-btn"
            >
              {submitting ? (
                'Enviando...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Solicitud de Verificación
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default AuthorVerification;
